/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { space } from 'styled-system';
import { Flex } from '@rebass/grid';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { v4 as uuidv4 } from 'uuid';

import htmlParser from '../utils/htmlParser';
import { fixPropWarnings } from '../utils/fixPropWarnings';

import * as Texts from './Text';
import RichText from './RichTextV2';
import MobileSteps from './MobileSteps';
import Svg from './Svg';
import BladeMenu from './BladeMenu';
import * as Link from './Link';
import CallToAction from './CallToAction';
import InlineLink from './InlineLink';

interface InsuranceBtnProps {
  isActive: boolean;
  m: string[];
}

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: -10px;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* IE10+ CSS styles go here */
    display: block;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    margin: -20px;
  }
`;

export const InsuranceBtn = styled.button.withConfig(fixPropWarnings('isActive', 'variant'))<InsuranceBtnProps>`
  flex: 1 1 100%;
  /*  */

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border: ${themeGet('borders.card')};
  background-color: ${(props) => props.isActive ? themeGet('colors.greys.0') : themeGet('colors.white')};
  padding: 2rem 0.75rem;

  :focus,
  :hover {
    background-color: ${themeGet('colors.greys.0')};
  }

  img {
    height: 30px;
    width: 30px;
  }

  @media only screen and (min-width: 679px) {
    padding: 2rem;
    img {
      height: 35px;
      width: 35px;
    }
  }

  @media only screen and (min-width: 1279px) {
    img {
      height: 40px;
      width: 40px;
    }
  }

  /*  */
  ${space}
`;

export const CardTitle = styled.p`
  font-size: 18px;
  font-weight: ${themeGet('fontWeights.semibold')};
  text-align: left;
  margin: 0.5rem 0 0 0;

  @media only screen and (min-width: 679px) {
    font-size: 25px;
  }
`;

const options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => (
      <Texts.Card key={uuidv4()} m={['0 0 2rem']}>
        {children}
      </Texts.Card>
    ),
    [INLINES.HYPERLINK]: (node: any, children: any) => (
      <Link.Primary key={uuidv4()} href={node.data.uri} fontSize="inherit">
        {children}
      </Link.Primary>
    ),
  },
};

interface ClaimBladeProps {
  className?: string;
  cta: any;
  title: string;
  closeMsg: string;
  intro: any;
  menu: any[];
  onKeyDown?: () => void;
}

const ClaimBlade = ({
  className,
  cta,
  title,
  closeMsg,
  intro,
  menu,
  onKeyDown,
}: ClaimBladeProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const [bladeState, setBladeState] = useState(false);

  const updateBladeState = () => setBladeState(!bladeState);

  const resetBladeState = () => setBladeState(false);

  const closeBladeOnEsc = (e: any) => {
    const event = e || window.event;
    if (event.key === 'Escape' || event.keyCode === 27) {
      setBladeState(false);
    }
  };

  useEffect(() => {
    resetBladeState();
  }, []);

  return (
    <>
      {cta && cta.internal.type === 'ContentfulButton' ? (
        <CallToAction
          key={cta.id}
          className={className}
          variant={cta.buttonType}
          onClick={updateBladeState}
          onKeyDown={onKeyDown}
          aria-expanded={bladeState}
        >
          {cta.buttonLabel}
        </CallToAction>
      ) : (
        <InlineLink
          key={cta.id}
          className={className}
          onClick={updateBladeState}
          onKeyDown={onKeyDown}
          aria-expanded={bladeState}
        >
          {cta.linkText}
        </InlineLink>
      )}
      <BladeMenu
        title={title}
        closeMsg={closeMsg}
        isOpen={bladeState}
        action={updateBladeState}
        closeAllMenusOnEsc={closeBladeOnEsc}
        resetState={resetBladeState}
      >
        <Wrapper>
          {intro && <RichText document={intro} options={options} />}
          <Flex
            flexDirection={['row']}
            m={['-5px', '-5px', '-10px']}
            alignItems="stretch"
          >
            {menu.map(({ panelIcon }, index) => {
              const isActive = currentIndex === index;
              const cardTitle = panelIcon.iconText.replace(' ', '<br />');
              return (
                <InsuranceBtn
                  key={panelIcon.id}
                  type="button"
                  m={['5px', '5px', '10px']}
                  isActive={isActive}
                  onClick={() => {
                    setCurrentIndex(index);
                  }}
                  aria-selected={isActive}
                >
                  {!isActive && panelIcon.optionalSecondaryIconDam ? (
                    <Svg
                      src={
                        panelIcon.optionalSecondaryIconDam.asset.localImage
                          .publicURL
                      }
                      alt={panelIcon.optionalSecondaryIconDam.altText}
                    />
                  ) : (
                    <Svg
                      src={panelIcon.iconImageDam.asset.localImage.publicURL}
                      alt={panelIcon.iconImageDam.altText}
                    />
                  )}
                  <CardTitle>{htmlParser(cardTitle)}</CardTitle>
                </InsuranceBtn>
              );
            })}
          </Flex>
          {menu.map((tabbedPanel, index) => {
            const isActive = currentIndex === index;
            if (!isActive) return null;
            return <MobileSteps key={tabbedPanel.id} data={tabbedPanel} />;
          })}
        </Wrapper>
      </BladeMenu>
    </>
  );
};

export default ClaimBlade;
